import { Input } from "@nextui-org/react";
import { ChangeEvent, FC, useEffect, useState } from "react";

type ZNumberFieldProps = {
  value: string | number;
  onChange?: (value: string) => string | void;
  title?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  isDisabled?: boolean;
  showTitle?: boolean;
};

const ZNumberField: FC<ZNumberFieldProps> = ({
  value,
  onChange,
  title,
  placeholder = "Enter a number",
  min,
  max,
  isDisabled = false,
  showTitle = true,
}) => {
  const [inputValue, setInputValue] = useState(String(value ?? ""));

  useEffect(() => {
    const valueStr = String(value ?? "");
    if (inputValue !== valueStr) {
      setInputValue(valueStr);
    }
  }, [value, inputValue]);



  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (raw === "") {
      setInputValue("");
      onChange?.("");
      return;
    }

    // Reject any non-numeric characters
    if (!/^\d+$/.test(raw)) return;

    let digits = raw.split("");
    let finalValue = "";

    for (let i = digits.length - 1; i >= 0; i--) {
      const digit = Number(digits[i]);
      const isValidMin = min === undefined || digit >= min;
      const isValidMax = max === undefined || digit <= max;

      if (isValidMin && isValidMax) {
        finalValue = digit.toString();
        break;
      }
    }

    if (finalValue !== "") {
      setInputValue(finalValue);
      const updated = onChange?.(finalValue);
      if (typeof updated === "string") {
        setInputValue(updated);
      }
    } else {
      // Reset to empty if no digit satisfies min/max
      setInputValue("");
      onChange?.("");
    }
  };

  return (
    <div className="relative">
      {title && showTitle && (
        <div className="bg-[#F9F7F7] text-[#717171] font-medium py-2 px-4 text-[10px] leading-4 border-y border-[#E4E4E7]">
          {title}
        </div>
      )}
      <Input
        autoFocus={true}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="text-[#121212] font-light bg-white"
        classNames={{
          base: "shadow-none border-none rounded-none truncate",
          inputWrapper: "border-[1px] rounded-md text-[#333333]",
          input: "!text-black",
        }}
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={isDisabled}
        variant="bordered"
      />
    </div>
  );
};

export default ZNumberField;
